import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person,
  CalendarToday,
  AttachMoney,
  Home,
  LocationOn,
  Group,
  Payment,
  People,
  Message,
  Settings,
} from '@mui/icons-material';
import { Booking, Listing } from '@/types';
import { BookingsService } from '@/utils/bookings-service';
import { GuestDetailsPanel } from './GuestDetailsPanel';
import { BookingCommunicationPanel } from './BookingCommunicationPanel';
import { BookingAutomationStatus } from '../BookingAutomationStatus';

interface BookingDetailsProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  listing?: Listing;
  onEdit?: (booking: Booking) => void;
  onCancel?: (booking: Booking) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-details-tabpanel-${index}`}
      aria-labelledby={`booking-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export const BookingDetails: React.FC<BookingDetailsProps> = ({
  open,
  onClose,
  booking,
  listing,
  onEdit,
  onCancel,
}) => {

  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!booking) return null;

  const duration = BookingsService.calculateDuration(booking.startDate, booking.endDate);
  const dateRange = BookingsService.formatDateRange(booking.startDate, booking.endDate);
  const statusColor = BookingsService.getStatusColor(booking.status);

  const handleEdit = () => {
    onEdit?.(booking);
    onClose();
  };

  const handleCancel = () => {
    onCancel?.(booking);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            Booking Details
          </Typography>
          <Chip
            label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
            color={statusColor}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab 
              label="Booking Info" 
              icon={<Person />} 
              iconPosition="start"
            />
            <Tab 
              label="Guest Details" 
              icon={<People />} 
              iconPosition="start"
            />
            <Tab 
              label="Automation" 
              icon={<Settings />} 
              iconPosition="start"
            />
            <Tab 
              label="Communication" 
              icon={<Message />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Booking Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="primary" />
              Booking Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Booking ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  #{booking.bookingId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Guest ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.guestId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Created Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Property Information */}
          {listing && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Home color="primary" />
                Property Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Property Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {listing.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" />
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {listing.address?.street}, {listing.address?.city}, {listing.address?.state} {listing.address?.zipCode}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Bedrooms
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {listing.bedrooms}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Bathrooms
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {listing.bathrooms}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Max Guests
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {listing.maxGuests}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Stay Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="primary" />
              Stay Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-in Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.startDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-out Date
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.endDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {duration} night{duration !== 1 ? 's' : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Group fontSize="small" />
                  Number of Guests
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.guests}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="primary" />
              Payment Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  ${booking.totalPrice.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Payment fontSize="small" />
                  Payment Status
                </Typography>
                <Chip
                  label={booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                  color={booking.paymentStatus === 'paid' ? 'success' : booking.paymentStatus === 'refunded' ? 'error' : 'warning'}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {listing && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Price Breakdown
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      Base Rate: ${listing.pricing.basePrice} × {duration} nights
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      ${(listing.pricing.basePrice * duration).toFixed(2)}
                    </Typography>
                  </Grid>
                  {listing.pricing.cleaningFee && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        Cleaning Fee
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        ${listing.pricing.cleaningFee.toFixed(2)}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Paper>
        </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <GuestDetailsPanel 
            booking={booking}
            listingTitle={listing?.title}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BookingAutomationStatus 
              booking={{
                bookingId: booking.bookingId,
                listingId: booking.listingId,
                checkInFormUrl: booking.checkInFormUrl,
                checkInFormExpiration: booking.checkInFormExpiration,
                communicationLog: booking.communicationLog?.map(comm => ({
                  recordId: comm.recordId,
                  type: comm.type as 'email' | 'whatsapp' | 'sms',
                  recipient: comm.recipient,
                  status: comm.status as 'sent' | 'delivered' | 'failed',
                  sentAt: comm.sentAt
                })),
                status: booking.status,
                createdAt: booking.createdAt
              }}
              onRetrySuccess={() => {
                // Optionally refresh booking data here
                console.log('Automation retry successful');
              }}
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <BookingCommunicationPanel 
            booking={booking}
            listingTitle={listing?.title}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {(booking.status === 'pending' || booking.status === 'confirmed') && (
          <Button onClick={handleCancel} color="error">
            Cancel Booking
          </Button>
        )}
        <Button onClick={handleEdit} variant="contained">
          Edit Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingDetails;